// BookTickets/stages/DateAndTime/hooks/useSessionsForDate.ts
'use client'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { format as formatDate } from 'date-fns'

import type { Session } from '@booking/types/booking'
import type { BookingConfig } from '@booking/types/bookingConfig'
import { apiGetSessionsByDate } from '@booking/services/bookingApi'
import { isSessionListed, presaleNow } from '@booking/lib/presale'

type UseSessionsForDateArgs = {
  date: Date
  enabled: boolean
  sessionTags?: string[]
  /** The flow's config. Its context goes to the API as `flow`; both decide whether pre-sale dates are listed. */
  config: BookingConfig
  /** Listed even when held back, so a session restored from an abandoned cart stays visible. */
  selectedSessionId?: string
}

type UseSessionsForDateResult = {
  loading: boolean
  /** The last fetch failed. `sessions` is then empty, never another day's. */
  error: boolean
  sessions: Session[]
  refresh: () => Promise<void>
}

/**
 * Fetches sessions for a date and applies basic “future only” filtering, then
 * lists only what the flow sells (pre-sale dates, see lib/presale).
 * (Any extra filtering can later be injected via config, but kept out of the stage UI.)
 *
 * The list is emptied while a fetch runs and only the latest fetch may fill it,
 * so a slow reply for a day the user has already left can't be shown (and
 * booked) under the day they're on now.
 */
export function useSessionsForDate({
  date,
  enabled,
  sessionTags,
  config,
  selectedSessionId,
}: UseSessionsForDateArgs): UseSessionsForDateResult {
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState(false)
  const [sessions, setSessions] = useState<Session[]>([])
  const latestRequest = useRef(0)

  const isoDay = formatDate(date, 'yyyy-MM-dd')

  const refresh = useCallback(async () => {
    const request = ++latestRequest.current
    setLoading(true)
    setError(false)
    setSessions([])
    try {
      const list = await apiGetSessionsByDate(isoDay, undefined, { flow: config.context })
      if (request !== latestRequest.current) return
      if (!list) return

      const nowMs = Date.now()
      const futureOnly = list.availabilities.filter((s) => new Date(s.dateTime).getTime() > nowMs)

      const filtered =
        sessionTags?.length
          ? futureOnly.filter((s) => sessionTags.every((tag) => s.tags?.includes(tag)))
          : futureOnly

      setSessions(filtered as unknown as Session[])
    } catch {
      // Already reported to Sentry by instrumentedCall.
      if (request === latestRequest.current) setError(true)
    } finally {
      if (request === latestRequest.current) setLoading(false)
    }
  }, [isoDay, config.context, sessionTags])

  useEffect(() => {
    if (!enabled) return
    void refresh()
  }, [enabled, refresh])

  const listed = useMemo(() => {
    const now = presaleNow()
    return sessions.filter((s) => s.id === selectedSessionId || isSessionListed(s, config, now))
  }, [sessions, config, selectedSessionId])

  return { loading, error, sessions: listed, refresh }
}
